import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Chip,
  Stack,
  Button,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  InputAdornment,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import InfoIcon from "@mui/icons-material/Info";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Grid from "@mui/material/Grid2";
import { useParams, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import ParentWrapper from "../../../components/ParentWrapper";
import Loader from "../../../components/AnimatedLoader";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import axiosInstance from "../../../services/axiosInterceptors";
import { palette, theme as colorTheme } from "../../../Convoze.theme";

const positive = colorTheme("palette", "positive").value;
const positiveBg = colorTheme("palette", "positiveBg").value;
const negative = colorTheme("palette", "negative").value;
const negativeBg = colorTheme("palette", "negativeBg").value;
const neutral = colorTheme("palette", "neutral").value;
const neutralBg = colorTheme("palette", "neutralBg").value;
const primary = colorTheme("palette", "primary").value;

const COMPANIES_WITH_SCORING = [
  "mPokket", "1Finance", "Auric", "VTrans", "Roposo",
  "vxpressnmc", "EBMS Business (India)", "Expert Plus",
  "Positive Zone", "EBMS Business – RAKEZ Free Zone",
];

const QaEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const decodedToken = token ? jwtDecode(token) : {};
  const showScoringFeatures = COMPANIES_WITH_SCORING.includes(
    decodedToken?.companyName
  );

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [qaParameters, setQaParameters] = useState({});
  const [editedData, setEditedData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [statusSummary, setStatusSummary] = useState({
    mostCritical: false,
    critical: false,
    nonCritical: false,
  });

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axiosInstance.get(
          `${apiEndPointsConfig?.interaction}/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        const rawQa = res?.data?.data[0]?.callAdherence[0] || {};
        setQaParameters(rawQa);
        setEditedData(JSON.parse(JSON.stringify(rawQa)));
        setLoading(false);
      } catch (error) {
        console.error("Error fetching QA parameters:", error);
        setLoading(false);
        setSnackbar({
          open: true,
          message: "Failed to load QA parameters",
          severity: "error",
        });
      }
    };
    fetchData();
  }, [id]);

  // Recompute status summary whenever editedData changes
  useEffect(() => {
    if (!editedData || Object.keys(editedData).length === 0) return;
    const sections = Object.values(editedData);
    const checkPass = (criticalityType) => {
      const filtered = sections?.filter(
        (s) => s?.criticality === criticalityType
      );
      if (!filtered || filtered.length === 0) return false;
      return !filtered.some(
        (s) => String(s?.overAllIssueFlagged) === "false"
      );
    };
    setStatusSummary({
      mostCritical: checkPass("MostCritical"),
      critical: checkPass("Critical"),
      nonCritical: checkPass("NonCritical"),
    });
  }, [editedData]);

  // Sort QA parameters
  const sortQAParameters = (obj) => {
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) return {};
    return Object.fromEntries(
      Object.entries(obj).sort(
        ([, a], [, b]) =>
          Number(a?.parameterIndex || 0) - Number(b?.parameterIndex || 0)
      )
    );
  };

  // Handle parameter-level field change
  const handleParameterChange = (sectionKey, field, value) => {
    setEditedData((prev) => {
      const updated = { ...prev };
      updated[sectionKey] = { ...updated[sectionKey], [field]: value };
      return updated;
    });
    setHasChanges(true);
  };

  // Handle sub-parameter field change
  const handleSubParamChange = (sectionKey, subIdx, field, value) => {
    setEditedData((prev) => {
      const updated = { ...prev };
      const subParams = [...(updated[sectionKey]?.subParameters || [])];
      subParams[subIdx] = { ...subParams[subIdx], [field]: value };
      updated[sectionKey] = { ...updated[sectionKey], subParameters: subParams };
      return updated;
    });
    setHasChanges(true);
  };

  // Save handler
  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axiosInstance.put(
        `${apiEndPointsConfig.updateQaParameters}/${id}`,
        { qaParameters: editedData },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const resData = response?.data;
      setQaParameters(JSON.parse(JSON.stringify(editedData)));
      setIsEditing(false);
      setHasChanges(false);
      setSnackbar({
        open: true,
        message: resData?.message || "QA parameters updated successfully!",
        severity: "success",
      });
    } catch (error) {
      console.error("Error updating QA parameters:", error);
      const errMsg =
        error?.response?.data?.message || "Failed to update QA parameters";
      setSnackbar({
        open: true,
        message: errMsg,
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  // Cancel editing
  const handleCancel = () => {
    setEditedData(JSON.parse(JSON.stringify(qaParameters)));
    setIsEditing(false);
    setHasChanges(false);
  };

  // Toggle edit mode
  const toggleEdit = () => {
    if (isEditing) {
      handleCancel();
    } else {
      setIsEditing(true);
    }
  };

  const sortedData = sortQAParameters(editedData);
  const hasMostCritical =
    sortedData &&
    Object.values(sortedData).some((s) => s?.criticality === "MostCritical");
  const hasCritical =
    sortedData &&
    Object.values(sortedData).some((s) => s?.criticality === "Critical");
  const hasNonCritical =
    sortedData &&
    Object.values(sortedData).some((s) => s?.criticality === "NonCritical");
  const noSectionsAvailable =
    !hasMostCritical && !hasCritical && !hasNonCritical;

  // Render a criticality section
  const renderCriticalitySection = (criticalityType, label, color, statusKey) => {
    const filtered = Object.entries(sortedData).filter(
      ([_, sectionData]) => sectionData?.criticality === criticalityType
    );
    if (filtered.length === 0) return null;

    return (
      <Grid
        item
        size={{ xs: 12, md: 12 }}
        sx={{
          borderRadius: 6,
          border: "1px solid #EEEEEE",
          p: 3,
          mt: criticalityType !== "MostCritical" ? 3 : 0,
          background: "#fff",
        }}
      >
        <Stack
          direction="row"
          gap={2}
          mb={2}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="body1"
            fontWeight={600}
            gutterBottom
            color={color}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {label}
            <Tooltip
              title={
                criticalityType === "MostCritical"
                  ? "Key parameters— Standards must be followed"
                  : criticalityType === "Critical"
                  ? "Important parameters— Should be closely monitored"
                  : "Supporting parameters – Standards are important/encouraged to meet"
              }
            >
              <InfoIcon
                sx={{ fontSize: 18, color: "grey", cursor: "pointer" }}
              />
            </Tooltip>
          </Typography>

          <Box
            p={"5px 36px"}
            sx={{
              border: statusSummary?.[statusKey]
                ? `1px solid ${positive}`
                : `1px solid ${negative}`,
              borderRadius: 2,
              color: statusSummary?.[statusKey] ? positive : negative,
              fontWeight: "bold",
            }}
          >
            {statusSummary?.[statusKey] ? "Pass" : "Fail"}
          </Box>
        </Stack>

        {filtered.map(([sectionKey, sectionData], i) => (
          <Accordion
            key={i}
            sx={{
              mb: 1,
              borderRadius: "12px !important",
              "&:before": { display: "none" },
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body1" sx={{ flexGrow: 1, fontWeight: 500 }}>
                {sectionKey?.replace(/([a-z])([A-Z])/g, "$1 $2")}
              </Typography>

              {/* Editable Overall Score */}
              {showScoringFeatures && (
                <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
                  <Typography sx={{ mr: 1, fontSize: "0.875rem" }}>
                    Overall Score:
                  </Typography>
                  {isEditing ? (
                    <TextField
                      size="small"
                      type="number"
                      value={sectionData?.parameterWeigthageCal ?? ""}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleParameterChange(
                          sectionKey,
                          "parameterWeigthageCal",
                          e.target.value
                        );
                      }}
                      sx={{
                        width: 130,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          "& fieldset": { borderColor: primary },
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                            / {sectionData?.originalParameterWeigthage}
                          </InputAdornment>
                        ),
                      }}
                      inputProps={{ style: { textAlign: "center", padding: "4px 8px" } }}
                    />
                  ) : (
                    <Typography fontWeight={700}>
                      {sectionData?.parameterWeigthageCal} / {sectionData?.originalParameterWeigthage}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Editable Parameter Status */}
              {isEditing ? (
                <Select
                  size="small"
                  value={String(sectionData?.overAllIssueFlagged)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    e.stopPropagation();
                    const val = e.target.value;
                    handleParameterChange(
                      sectionKey,
                      "overAllIssueFlagged",
                      val === "true" ? true : val === "false" ? false : val
                    );
                  }}
                  sx={{
                    mr: 3,
                    minWidth: 90,
                    borderRadius: "8px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: primary,
                    },
                  }}
                >
                  <MenuItem value="true">
                    <Chip label="Pass" size="small" sx={{ color: positive, border: `1px solid ${positive}`, background: "transparent", fontWeight: "bold" }} />
                  </MenuItem>
                  <MenuItem value="false">
                    <Chip label="Fail" size="small" sx={{ color: negative, border: `1px solid ${negative}`, background: "transparent", fontWeight: "bold" }} />
                  </MenuItem>
                  <MenuItem value="N/A">
                    <Chip label="N/A" size="small" sx={{ color: "grey", border: "1px solid grey", background: "transparent", fontWeight: "bold" }} />
                  </MenuItem>
                </Select>
              ) : (
                <Chip
                  label={
                    String(sectionData?.overAllIssueFlagged) === "true"
                      ? "Pass"
                      : String(sectionData?.overAllIssueFlagged) === "false"
                      ? "Fail"
                      : "N/A"
                  }
                  size="small"
                  sx={{
                    mr: 3,
                    color:
                      String(sectionData?.overAllIssueFlagged) === "true"
                        ? positive
                        : String(sectionData?.overAllIssueFlagged) === "false"
                        ? negative
                        : "grey",
                    border: "1px solid",
                    borderColor:
                      String(sectionData?.overAllIssueFlagged) === "true"
                        ? positive
                        : String(sectionData?.overAllIssueFlagged) === "false"
                        ? negative
                        : "grey",
                    background: "transparent",
                    fontWeight: "bold",
                  }}
                />
              )}
            </AccordionSummary>

            <AccordionDetails>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        backgroundColor: "#f8f8fc",
                      }}
                    >
                      <TableCell><strong>Sub Parameter</strong></TableCell>
                      <TableCell><strong>Expectation</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Reason</strong></TableCell>
                      {showScoringFeatures && (
                        <TableCell><strong>AI Score</strong></TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sectionData?.subParameters?.map((param, idx) => (
                      <TableRow
                        key={idx}
                        sx={{
                          "&:hover": {
                            backgroundColor: isEditing
                              ? "rgba(118, 53, 221, 0.03)"
                              : "transparent",
                          },
                          transition: "background-color 0.2s",
                        }}
                      >
                        {/* Sub Parameter Name (read-only) */}
                        <TableCell>
                          <Tooltip title={param?.description}>
                            <IconButton size="small" sx={{ color: primary }}>
                              <InfoOutlined
                                sx={{ color: primary, fontSize: 20 }}
                              />
                            </IconButton>
                          </Tooltip>
                          {param?.parameter}
                        </TableCell>

                        {/* Expectation (read-only) */}
                        <TableCell>{param?.expectation}</TableCell>

                        {/* Editable Status */}
                        <TableCell>
                          {isEditing ? (
                            <Select
                              size="small"
                              value={String(param?.issueFlagged)}
                              onChange={(e) =>
                                handleSubParamChange(
                                  sectionKey,
                                  idx,
                                  "issueFlagged",
                                  e.target.value === "true"
                                    ? true
                                    : e.target.value === "false"
                                    ? false
                                    : e.target.value
                                )
                              }
                              sx={{
                                minWidth: 90,
                                borderRadius: "8px",
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: primary,
                                },
                              }}
                            >
                              <MenuItem value="true">
                                <Chip label="Pass" size="small" sx={{ color: positive, border: `1px solid ${positive}`, background: "transparent" }} />
                              </MenuItem>
                              <MenuItem value="false">
                                <Chip label="Fail" size="small" sx={{ color: negative, border: `1px solid ${negative}`, background: "transparent" }} />
                              </MenuItem>
                              <MenuItem value="N/A">
                                <Chip label="N/A" size="small" sx={{ color: "grey", border: "1px solid grey", background: "transparent" }} />
                              </MenuItem>
                            </Select>
                          ) : (
                            <Chip
                              label={
                                String(param?.issueFlagged) === "true"
                                  ? "Pass"
                                  : String(param?.issueFlagged) === "false"
                                  ? "Fail"
                                  : "N/A"
                              }
                              size="small"
                              sx={{
                                border: "1px solid",
                                borderColor:
                                  String(param?.issueFlagged) === "true"
                                    ? positive
                                    : String(param?.issueFlagged) === "false"
                                    ? negative
                                    : colorTheme("palette", "secondary").value,
                                color:
                                  String(param?.issueFlagged) === "true"
                                    ? positive
                                    : String(param?.issueFlagged) === "false"
                                    ? negative
                                    : colorTheme("palette", "secondary").value,
                                backgroundColor: "transparent",
                                fontWeight: "medium",
                              }}
                            />
                          )}
                        </TableCell>

                        {/* Editable Reason */}
                        <TableCell sx={{ maxWidth: 350 }}>
                          {isEditing ? (
                            <TextField
                              size="small"
                              multiline
                              maxRows={4}
                              fullWidth
                              value={param?.reason || ""}
                              onChange={(e) =>
                                handleSubParamChange(
                                  sectionKey,
                                  idx,
                                  "reason",
                                  e.target.value
                                )
                              }
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                  "& fieldset": { borderColor: primary },
                                },
                              }}
                            />
                          ) : (
                            <Typography variant="body2">
                              {param?.reason}
                            </Typography>
                          )}
                        </TableCell>

                        {/* Editable AI Score */}
                        {showScoringFeatures && (
                          <TableCell>
                            {isEditing ? (
                              <TextField
                                size="small"
                                type="number"
                                value={param?.calculatedWeigthage ?? ""}
                                onChange={(e) =>
                                  handleSubParamChange(
                                    sectionKey,
                                    idx,
                                    "calculatedWeigthage",
                                    e.target.value
                                  )
                                }
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      / {param?.originalWeightage}
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  width: 130,
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                    "& fieldset": { borderColor: primary },
                                  },
                                }}
                                inputProps={{ style: { textAlign: "center", padding: "4px 8px" } }}
                              />
                            ) : (
                              <Typography sx={{ fontWeight: 600 }}>
                                {param?.calculatedWeigthage}/
                                {param?.originalWeightage}
                              </Typography>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ))}
      </Grid>
    );
  };

  if (loading) {
    return (
      <ParentWrapper>
        <Loader />
      </ParentWrapper>
    );
  }

  return (
    <ParentWrapper>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              color: primary,
              "&:hover": { backgroundColor: "rgba(118, 53, 221, 0.08)" },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography
              variant="h5"
              component="h1"
              fontWeight="bold"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              Edit QA Parameters
              {isEditing && (
                <Chip
                  label="EDITING"
                  size="small"
                  sx={{
                    bgcolor: primary,
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "0.7rem",
                    animation: "pulse 2s infinite",
                    "@keyframes pulse": {
                      "0%": { opacity: 1 },
                      "50%": { opacity: 0.7 },
                      "100%": { opacity: 1 },
                    },
                  }}
                />
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Transcription ID: {id}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5 }}>
          {!isEditing ? (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={toggleEdit}
              sx={{
                textTransform: "none",
                bgcolor: primary,
                borderRadius: "10px",
                px: 3,
                fontWeight: 600,
                boxShadow: `0 4px 14px ${primary}44`,
                "&:hover": {
                  bgcolor: "#6b23e0",
                  boxShadow: `0 6px 20px ${primary}66`,
                },
              }}
            >
              Edit Parameters
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                sx={{
                  textTransform: "none",
                  borderColor: negative,
                  color: negative,
                  borderRadius: "10px",
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: negative,
                    backgroundColor: negativeBg,
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={
                  saving ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SaveIcon />
                  )
                }
                onClick={handleSave}
                disabled={saving || !hasChanges}
                sx={{
                  textTransform: "none",
                  bgcolor: positive,
                  borderRadius: "10px",
                  px: 3,
                  fontWeight: 600,
                  boxShadow: `0 4px 14px ${positive}44`,
                  "&:hover": {
                    bgcolor: "#0e7a4a",
                    boxShadow: `0 6px 20px ${positive}66`,
                  },
                  "&.Mui-disabled": {
                    bgcolor: "#ccc",
                    color: "#fff",
                  },
                }}
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, ml: 7 }}>
        Review and manually edit QA parameter statuses, reasons, and scores for
        this interaction.
      </Typography>

      {/* Change indicator */}
      {hasChanges && isEditing && (
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 2,
            ml: 7,
            borderRadius: "12px",
            background: `linear-gradient(135deg, ${neutralBg} 0%, #fff8e1 100%)`,
            border: `1px solid ${neutral}33`,
            display: "flex",
            alignItems: "center",
            gap: 1,
            maxWidth: 450,
          }}
        >
          <CheckCircleOutlineIcon sx={{ color: neutral, fontSize: 20 }} />
          <Typography variant="body2" fontWeight={500} color="text.secondary">
            You have unsaved changes. Click <strong>Save Changes</strong> to
            persist your edits.
          </Typography>
        </Paper>
      )}

      {/* QA Sections */}
      <Box sx={{ width: "100%", pl: 7 }}>
        {noSectionsAvailable ? (
          <Box sx={{ textAlign: "center", py: 10 }}>
            <Typography variant="h6" color="textSecondary">
              No QA parameters available
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={1}>
            {renderCriticalitySection(
              "MostCritical",
              "Most Critical",
              negative,
              "mostCritical"
            )}
            {renderCriticalitySection(
              "Critical",
              "Critical",
              negative,
              "critical"
            )}
            {renderCriticalitySection(
              "NonCritical",
              "Non Critical",
              positive,
              "nonCritical"
            )}
          </Grid>
        )}
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            borderRadius: "12px",
            fontWeight: 600,
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ParentWrapper>
  );
};

export default QaEditPage;
